import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useMyTasksToday } from '../../hooks/workspace';
import { toast } from '../../store/toastStore';
import { announce } from '../ui/LiveAnnouncer';
import { apiErrorMessage } from '../sales/shared';
import { OPEN_TICKET_STATUS_PARAM } from '../../utils/ticketStatus';
import type { DailyStatusTicket } from '../../types/workspace';
import type { ActiveTimer, PausedTimerInfo, Ticket } from '../../types';
import { formatClock, groupFocus, homeRoleFrom, localDateKey } from './homeModel';
import { usePermissions } from '../../hooks/usePermissions';
import { attendanceQuestion, engagementRequiredOptions, isAutoClockoutRefusal, isClockInRequired, isOnBreakError, resumePausedOnSwitch, switchCandidates } from '../timer/timerModel';
import { askBackToWorkFirst, askClockInFirst, askEndBreakFirst, askTimerSwitch, askWorkEngagement } from '../timer/timerFlow';
import { saveLocation } from '../attendance/ClockInChoice';
import { appLocale } from '../../utils/format';
import { isMonthApprovalRequired, monthOfApprovalError } from '../monthApproval/monthApprovalModel';
import { useMonthApprovalNotice } from '../monthApproval/useMonthApprovalNotice';
import { MONTH_APPROVAL_STATUS_KEY } from '../monthApproval/useMonthApprovalStatus';

export const FOCUS_FETCH_LIMIT = 100;
const FOCUS_SORT = 'priority,due_date,stale' as const;

function mapTicketToRow(t: Ticket): DailyStatusTicket {
  return {
    id: t.id,
    title: t.title,
    status: t.status,
    priority: t.priority,
    task_type: t.task_type,
    customer_summary: t.customer_summary,
    organization_id: t.organization_id,
    project_id: t.project_id,
    due_date: t.due_date,
    days_stale: 0,
    organization_name: t.organization_name ?? null,
    project_name: t.project_name ?? null,
    engagement_id: t.engagement_id ?? null,
    engagement_type: t.engagement_type ?? null,
    engagement_name: t.engagement_name ?? null,
    assigned_agent_name: t.agent_name ?? null,
  };
}

/** Who sees what on Home — FU-44: by the server's identity (`/permissions/me`), the claim only as a fallback. */
export function useHomeRole() {
  const role = useAuthStore((s) => s.user?.role);
  const perms = usePermissions();
  const r = homeRoleFrom({
    role,
    isOteEmployee: perms.isOteEmployee,
    isGeneralManager: perms.capabilities.isGeneralManager,
    serverAnswered: perms.source === 'server',
    internalOnly: perms.internalOnly,
  });
  return { role, ...r };
}

/**
 * The focus list's data, shared by the header (next task, today's count) and
 * the list (same react-query keys as before, so nothing is fetched twice).
 */
export function useFocusData() {
  const user = useAuthStore((s) => s.user);
  const { isStaff } = useHomeRole();

  const staffQuery = useMyTasksToday({ limit: FOCUS_FETCH_LIMIT, sort: FOCUS_SORT, mine: true }, isStaff);
  const customerQuery = useQuery({
    queryKey: ['workspace', 'customer-tasks', user?.id],
    queryFn: async () => {
      const res = await api.getTickets({ limit: FOCUS_FETCH_LIMIT, status: OPEN_TICKET_STATUS_PARAM });
      return (res.data ?? []).map(mapTicketToRow);
    },
    enabled: !!user && !isStaff,
    staleTime: 30_000,
  });
  const query = isStaff ? staffQuery : customerQuery;
  const todayKey = localDateKey(new Date());
  const sections = useMemo(() => groupFocus(query.data ?? [], todayKey), [query.data, todayKey]);
  return { ...query, items: query.data ?? [], sections, todayKey };
}

/** Timer state from the shared `timer-status` query (same key and cadence as GlobalTimer). */
export function useTimerState() {
  const { isStaff } = useHomeRole();
  const q = useQuery({
    queryKey: ['timer-status'],
    queryFn: () => api.getTimerStatus(),
    enabled: isStaff,
    refetchInterval: 30_000,
    staleTime: 0,
  });
  const timers = q.data?.data?.timers ?? [];
  const running = timers.find((tm) => tm.status === 'running') ?? null;
  // FIX-OPENREC — the open work records (no timer on them), shown in Home's header.
  const openRecords = q.data?.data?.open_records ?? [];
  return { isLoading: q.isLoading && isStaff, running, timers, openRecords };
}

export interface StartTimerTask {
  id: string;
  title: string;
  /**
   * Tasks the user already chose to run together with this one (the break
   * resume starts several at once): their timers never trigger the
   * "stop and switch / run both" question.
   */
  runAlongside?: string[];
  /**
   * P-37 (D-194 Q-172 / Q-182) — "אני על זה" on a ticket: a reply timer. The
   * same clock-in / break / engagement questions, but no "stop and switch"
   * question: the server pauses the running task timers itself (they run
   * again after "גמרתי"; the page offers "שניהם במקביל").
   */
  reply?: boolean;
}

export interface UseStartTimerOptions {
  /** The caller shows the error itself (inline); no error toast. */
  silentErrors?: boolean;
  /** After the timer started (not when the user cancelled the switch question). */
  onStarted?: (task: StartTimerTask, started?: StartedTimerInfo) => void;
}

/** P-37 — what a start returned that the page may show (the paused task timers). */
export interface StartedTimerInfo {
  timerId: string | null;
  paused: PausedTimerInfo[];
}

/**
 * ▶ "התחלת טיימר" on a task: the ONE way the app starts a timer (Home, the
 * focus list, task lists, the ticket page, the timer dialog).
 *
 * The server lets several timers run (B.18). When another task's timer is
 * running, it first asks (owner 2026-10-11): "עצירת ‹א› ומעבר" (primary) or
 * "להריץ את שניהם". "Stop and switch" stops the other timer exactly like
 * "עצירת מקטע" in the stop dialog: the time is saved as a work log and its
 * work record stays open, to document or close later (nothing is lost, and no
 * summary is asked for in the middle of starting work).
 *
 * No clock-in today (P-10a, D-66): it first asks "החתמת כניסה (משרד)
 * והתחלה" / "החתמת כניסה (בית) והתחלה" / "ביטול" — there is no "timer only";
 * the server refuses a timer without a clock-in (409 CLOCK_IN_REQUIRED), and
 * when the client's attendance status was stale that refusal asks the same
 * question and retries once. That question comes before the switch question.
 *
 * P-10a (D-63): every entry point starts work through here — the floating
 * button's "התחל עבודה" window, ▶ on every task row, the task page, Home, and
 * the resume after a break — so the windows, texts and order are the same.
 */
export function useStartTimer(opts: UseStartTimerOptions = {}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const monthNotice = useMonthApprovalNotice();
  const optsRef = useRef(opts);
  optsRef.current = opts;
  return useMutation({
    mutationFn: async (task: StartTimerTask) => {
      const stopped: ActiveTimer[] = [];
      let clockedInAt: string | null = null;

      /** D-116 — already clocked out today: "חזרה לעבודה" instead of a second clock-in. */
      let canReturn = false;
      /** When today's clock-out was (the D-129 question says it). */
      let clockedOutAt: string | null = null;
      /** QX-1283 — the 00:00 automatic clock-out closed yesterday's shift: the question says so. */
      let afterAutoClockout = false;
      const readAttendance = (att: Awaited<ReturnType<typeof api.getAttendanceStatus>> | undefined) => {
        if (att?.data?.after_auto_clockout) afterAutoClockout = true;
        canReturn = att?.data?.status === 'clocked_out' && !!att?.data?.can_return_to_work;
        clockedOutAt = canReturn ? att?.data?.since ?? null : null;
      };

      /** D-66: ask office / home and clock in. False = cancelled or failed (nothing starts). */
      const clockInFirst = async (): Promise<boolean> => {
        if (canReturn) {
          // D-129 / QX-59: ASK first — [חזרה לעבודה והתחלה] [ביטול]; never back to work silently.
          if (!(await askBackToWorkFirst({ task, clockedOutAt }))) return false;
          try {
            await api.backToWork();
            canReturn = false;
            toast('success', t('attendance.backToWorkDone'));
            queryClient.invalidateQueries({ queryKey: ['attendance-status'] });
            return true;
          } catch (err) {
            toast('error', apiErrorMessage(err, t('attendance.backToWorkFailed')));
            return false;
          }
        }
        const answer = await askClockInFirst({ task, autoClockout: afterAutoClockout });
        if (!answer) return false;
        saveLocation(answer);
        try {
          const res = await api.clockIn(undefined, answer);
          clockedInAt = typeof res?.data?.recorded_at === 'string' ? res.data.recorded_at : new Date().toISOString();
          return true;
        } catch (err) {
          // The clock-in failed: say so. No timer without a clock-in (D-66).
          toast('error', apiErrorMessage(err, t('attendance.clockInFailed')));
          return false;
        }
      };

      /** D-98: "את/ה בהפסקה. לסיים אותה ולהתחיל?" — end the break first. False = cancelled or failed. */
      let breakEnded = false;
      const endBreakFirst = async (): Promise<boolean> => {
        if (!(await askEndBreakFirst({ task }))) return false;
        try {
          await api.endBreak();
          breakEnded = true;
          queryClient.invalidateQueries({ queryKey: ['attendance-status'] });
          return true;
        } catch (err) {
          toast('error', apiErrorMessage(err, t('attendance.breakEndFailed')));
          return false;
        }
      };

      let question: 'clock_in' | 'end_break' | null = null;
      try {
        // The shared attendance query (the top-bar control), fresh enough to ask.
        const att = await queryClient.fetchQuery({
          queryKey: ['attendance-status'],
          queryFn: () => api.getAttendanceStatus(),
          staleTime: 10_000,
        });
        question = attendanceQuestion(att?.data?.status);
        readAttendance(att);
      } catch {
        /* advisory: the server still decides (409 CLOCK_IN_REQUIRED / ON_BREAK below) */
      }
      if (question === 'clock_in' && !(await clockInFirst())) return { started: false, stopped, clockedInAt, breakEnded };
      if (question === 'end_break' && !(await endBreakFirst())) return { started: false, stopped, clockedInAt, breakEnded };

      let timers: ActiveTimer[] = [];
      try {
        // Fresh enough to ask the right question (the shared GlobalTimer query, 10 s).
        const status = await queryClient.fetchQuery({
          queryKey: ['timer-status'],
          queryFn: () => api.getTimerStatus(),
          staleTime: 10_000,
        });
        timers = status?.data?.timers ?? [];
      } catch {
        /* the status is advisory: the server still decides */
      }
      // DQ-040 - ▶ on a task whose timer already runs: say so. Before, it asked "עצירה ומעבר / להריץ את שניהם"
      // about the OTHER timers as if a new one would start, and then the server refused ("טיימר כבר פועל").
      if (!task.reply && timers.some((tm) => tm.status === 'running' && tm.kind !== 'reply' && tm.ticket_id === task.id)) {
        toast('info', t('timer.alreadyRunningOnTask', { title: task.title }));
        return { started: false, stopped, clockedInAt, breakEnded };
      }
      // FIX-P37 (T-69) — "אני על זה" asks the same question when another "אני על זה" runs.
      const others = switchCandidates(timers, task.id, { reply: task.reply, runAlongside: task.runAlongside });
      let runBoth = false;
      if (others.length > 0) {
        const choice = await askTimerSwitch({ task, others });
        if (!choice) return { started: false, stopped, clockedInAt, breakEnded };
        runBoth = choice !== 'switch';
        if (choice === 'switch') {
          for (const tm of others) {
            // FIX-P37 — leaving "אני על זה" for another task: what it paused stays paused.
            await api.stopTimer(tm.id, undefined, { resumePaused: resumePausedOnSwitch(tm, !!task.reply) });
            stopped.push(tm);
          }
        }
      }
      // D-146 (Q-98) — a ticket without an engagement: ask which one, then start on it.
      let engagementId: string | undefined;
      const send = (): Promise<{ data?: { debt_warning?: any; timer_id?: string; paused?: PausedTimerInfo[]; work_notice?: { kind?: string; over_quota?: boolean; before_engagement_start?: boolean } } }> => (task.reply
        ? api.startReplyTimer(task.id, { engagementId, parallel: runBoth })
        : api.startTimer(task.id, engagementId)) as any;
      const startOnce = async (): Promise<Awaited<ReturnType<typeof send>> | null> => {
        try {
          return await send();
        } catch (err) {
          const options = engagementRequiredOptions(err);
          if (options === null || engagementId) throw err;
          const chosen = await askWorkEngagement({ task, engagements: options });
          if (!chosen) return null;
          engagementId = chosen;
          return send();
        }
      };
      // D-110 — a customer in debt is not blocked; the server returns a warning.
      let started: Awaited<ReturnType<typeof send>> | null | undefined;
      try {
        started = await startOnce();
      } catch (err) {
        // A stale attendance status: the server says "no clock-in today" / "on a break" (D-98).
        if (isClockInRequired(err) && !clockedInAt) {
          if (isAutoClockoutRefusal(err)) afterAutoClockout = true;
          // The status was stale: read it again, so a clock-out today asks "חזרה לעבודה" (D-129), not a second clock-in.
          try {
            readAttendance(await queryClient.fetchQuery({
              queryKey: ['attendance-status'],
              queryFn: () => api.getAttendanceStatus(),
              staleTime: 0,
            }));
          } catch { /* the clock-in question below still applies */ }
          if (!(await clockInFirst())) return { started: false, stopped, clockedInAt, breakEnded };
        } else if (isOnBreakError(err) && !breakEnded) {
          if (!(await endBreakFirst())) return { started: false, stopped, clockedInAt, breakEnded };
        } else {
          // D-319 s.6 - a month to approve: the message with "לאישור {חודש}", and the card / window come back at once.
          if (isMonthApprovalRequired(err)) {
            void queryClient.invalidateQueries({ queryKey: MONTH_APPROVAL_STATUS_KEY });
            monthNotice(monthOfApprovalError(err));
            return { started: false, stopped, clockedInAt, breakEnded };
          }
          throw err;
        }
        started = await startOnce();
      }
      if (started === null) return { started: false, stopped, clockedInAt, breakEnded };
      const debtWarning = started?.data?.debt_warning?.message ?? null;
      // D-266 sec.1: the quota / bank is used up - the timer started; the hours will be saved and wait for approval.
      const notice = started?.data?.work_notice;
      const workNotice = [
        notice?.over_quota ? (notice.kind === 'hour_bank' ? 'consumption.bankLockedWillSave' : 'consumption.overQuotaWillSave') : null,
        notice?.before_engagement_start ? 'consumption.beforeStart' : null, // D-266 sec.3
      ].filter((k): k is string => !!k);
      const info: StartedTimerInfo = { timerId: started?.data?.timer_id ?? null, paused: started?.data?.paused ?? [] };
      return { started: true, stopped, clockedInAt, breakEnded, debtWarning, workNotice, info };
    },
    onSuccess: (res, task) => {
      if (!res.started) return;
      // D-110 — "⚠️ ללקוח X חוב פתוח (₪..., באיחור N ימים)": shown, never blocking.
      if ('debtWarning' in res && res.debtWarning) toast('warning', res.debtWarning);
      if ('workNotice' in res && res.workNotice) for (const k of res.workNotice) toast('warning', t(k)); // D-266
      if (res.clockedInAt) {
        toast('success', t('timer.clockIn.started', { time: formatClock(res.clockedInAt, appLocale(i18n.language)), title: task.title }));
      } else if (res.breakEnded) {
        toast('success', t('timer.onBreak.ended', { title: task.title }));
      }
      if (res.stopped.length === 1) {
        toast('success', t('timer.switch.switched', { stopped: res.stopped[0].ticket_title ?? '', title: task.title }));
      } else if (res.stopped.length > 1) {
        toast('success', t('timer.switch.switchedMany', { count: res.stopped.length, title: task.title }));
      } else if (!res.clockedInAt) {
        announce(t('home.timer.started', { title: task.title }));
      }
      optsRef.current.onStarted?.(task, 'info' in res ? res.info : undefined);
    },
    onError: (err) => {
      if (optsRef.current.silentErrors) return;
      // D-265 (QA-D265 minor): an archived engagement says so, in the user's language.
      const code = (err as { error?: { error_code?: string } } | null)?.error?.error_code;
      toast('error', code === 'ENGAGEMENT_ARCHIVED' ? t('home.timer.engagementArchived') : apiErrorMessage(err, t('home.timer.startFailed')));
    },
    onSettled: (res, err, task) => {
      queryClient.invalidateQueries({ queryKey: ['timer-status'] });
      // P-06/B (T-05) — a start moves a planning task (and its milestone) to "בביצוע".
      if (res?.started) {
        queryClient.invalidateQueries({ queryKey: ['ticket', task.id] });
        queryClient.invalidateQueries({ queryKey: ['all-tasks'] });
        queryClient.invalidateQueries({ queryKey: ['project'] });
        queryClient.invalidateQueries({ queryKey: ['milestone'] });
      }
      // A clock-in may have run even when the start after it failed.
      if (res?.clockedInAt || err) {
        queryClient.invalidateQueries({ queryKey: ['attendance-status'] });
        queryClient.invalidateQueries({ queryKey: ['attendance'] });
      }
      // A stop saves a segment (even when the start after it failed).
      if (err || res?.stopped.length) {
        queryClient.invalidateQueries({ queryKey: ['work-logs'] });
        queryClient.invalidateQueries({ queryKey: ['work-records'] });
      }
    },
  });
}
